/**
 * Kill switch and system switches (specs/c2-kontratlar.md 2.3, 2.4.4, 2.5, K6, K8, K12).
 *
 * The kill switch is written first and alone in its own transaction; undo and requeue
 * of the approvals in their undo window (stop only) come afterwards, best effort, and
 * never take the stop back.
 */
import { z } from 'zod';
import { countSchema, idStringSchema, isoDateTimeSchema, killSwitchLevelSchema, telegramUserIdSchema } from '../ids.js';
import { HOLD_TOKEN_MAX } from './decisions.js';
const reason = z.string().min(1).max(500);
/** PUT /api/v1/system/kill-switch. A null reason is stored as "<channel>: neden belirtilmedi". */
export const killSwitchRequestSchema = z.discriminatedUnion('channel', [
    z.strictObject({
        level: killSwitchLevelSchema,
        reason: reason.nullable(),
        channel: z.literal('panel'),
        /**
         * Required to leave stop for none or pause (0.3.0): aid = KILL_SWITCH_HOLD_AID,
         * ph = sha256(killSwitchResumeSubject()). The bot gets 403 hold_required there, so it
         * cannot step down through pause. Resuming from pause and tightening need none.
         */
        holdToken: z.string().min(1).max(HOLD_TOKEN_MAX).optional(),
    }),
    z.strictObject({
        level: killSwitchLevelSchema,
        reason: reason.nullable(),
        channel: z.literal('telegram'),
        telegramUserId: telegramUserIdSchema,
    }),
]);
/**
 * aid of the hold that resumes the team after a stop (POST /system/kill-switch/hold).
 * kill_switch is not a switch key (isSwitchKey), so no switch hold carries this aid.
 */
export const KILL_SWITCH_HOLD_AID = 'switch:kill_switch';
/** The text whose sha256 is ph of the resume-after-stop hold. */
export function killSwitchResumeSubject() {
    return 'switch:kill_switch:none';
}
/** Reason the API writes when the request carried none. */
export function defaultKillSwitchReason(channel) {
    return `${channel}: neden belirtilmedi`;
}
export const killSwitchResponseSchema = z.object({
    level: killSwitchLevelSchema,
    updatedAt: isoDateTimeSchema,
    /** Only for stop: approvals undone in their window and requeued under a new id. */
    requeued: z.array(z.object({ fromApprovalId: idStringSchema, toApprovalId: idStringSchema })),
    /** Approved work the executor will apply once the switch is back to none. */
    pendingExecutions: countSchema,
});
/** Platform codes of twin.platform_code (platform.<code> switches). */
export const PLATFORM_CODES = [
    'etsy',
    'instagram',
    'facebook_page',
    'pinterest',
    'youtube',
    'linkedin_profile',
    'linkedin_page',
    'tiktok',
    'x',
    'gmail_outreach',
    'gmail_shop',
    'google_drive',
    'github',
    'r2',
    'b2',
    'vertex',
    'site',
    'delivery',
    'anthropic',
    'genesis',
    'telegram',
    'apollo',
    'email_verifier',
    'healthchecks',
];
/** The fixed switch keys; platform.<code> is accepted only when that row exists in system_state. */
export const FIXED_SWITCH_KEYS = [
    'executor.dry_run',
    'feature.lead_engine',
    'feature.lead_verifier',
    'outreach.sending',
];
/**
 * Switches that cannot be turned on in C2 (422 not_available_in_c2). feature.lead_verifier left this list
 * in v0.1.78 (Sercan, 30 Sept 2026: verification opens); outreach.sending in v0.1.137, when the sending path
 * came: it opens only while a campaign is active (422 outreach_no_active_campaign).
 */
export const NOT_AVAILABLE_IN_C2_WHEN_ON = Object.freeze([]);
const PLATFORM_KEY = /^platform\.([a-z_]+)$/;
/** True for a key the switch endpoint accepts (platform codes still need their system_state row). */
export function isSwitchKey(key) {
    if (FIXED_SWITCH_KEYS.includes(key))
        return true;
    const code = PLATFORM_KEY.exec(key)?.[1];
    return code !== undefined && PLATFORM_CODES.includes(code);
}
/**
 * True when setting the switch to enabled makes the system less safe: turning dry run
 * off, or turning a feature, sending or a platform on. Loosening needs the panel and a
 * hold token; tightening may come from the bot too.
 */
export function switchLoosens(key, enabled) {
    return key === 'executor.dry_run' ? !enabled : enabled;
}
/** PUT /api/v1/system/switches/{key}. */
export const switchRequestSchema = z.discriminatedUnion('channel', [
    z.strictObject({
        enabled: z.boolean(),
        reason,
        channel: z.literal('panel'),
        holdToken: z.string().min(1).max(HOLD_TOKEN_MAX).optional(),
    }),
    z.strictObject({
        enabled: z.boolean(),
        reason,
        channel: z.literal('telegram'),
        telegramUserId: telegramUserIdSchema,
    }),
]);
export const switchResponseSchema = z.object({
    key: z.string().min(1),
    enabled: z.boolean(),
    updatedAt: isoDateTimeSchema,
});
/** POST /api/v1/system/switches/{key}/hold (response: HoldResponse). */
export const switchHoldRequestSchema = z.strictObject({ enabled: z.boolean() });
//# sourceMappingURL=system.js.map